import type { SupportedIntegration } from '@/components/integrations/integrations';
import { useLocation } from '@tanstack/react-router';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// Action types
interface CallAction {
  type: 'call';
  phoneNumber: string;
}

export interface DiscussionAction {
  type: 'discussion';
  entityId: string;
  entityType: 'contact' | 'company';
  phoneNumbers: string[];
}

interface IntegrationAction {
  type: 'integration';
  integration: SupportedIntegration;
  url: string;
}

interface CrmLinkAction {
  type: 'crm-link';
  url: string;
  label: string;
}

export type SidePanelAction = CallAction | DiscussionAction | IntegrationAction | CrmLinkAction;

// Entity info for displaying in the panel header. `name` is optional so
// nameless contacts can be passed through — the side-panel layout falls
// back to the localized "Contact" / "Company" title and `ContactAvatar`
// renders the neutral silhouette when no name reaches it.
export interface SidePanelEntityInfo {
  name?: string;
  imageUrl?: string;
}

interface SidePanelState {
  open: boolean;
  mode: 'contacts' | 'companies' | 'ai';
  context: {
    contactId?: string | null;
    companyId?: string | null;
  };
  actions?: SidePanelAction[];
  entityInfo?: SidePanelEntityInfo;
  readOnly?: boolean;
}

interface SidePanelContextType {
  sidePanelState: SidePanelState;
  setSidePanelState: (state: Partial<SidePanelState>) => void;
}

// Split contexts: components can subscribe to only what they need
// - State context: triggers re-render when state changes (for components that read state)
// - Dispatch context: stable reference, never triggers re-renders (for components that only write)
const SidePanelStateContext = createContext<SidePanelState | null>(null);
const SidePanelDispatchContext = createContext<((state: Partial<SidePanelState>) => void) | null>(
  null,
);

interface SidePanelProviderProps {
  children: React.ReactNode;
}

export const SidePanelProvider: React.FC<SidePanelProviderProps> = ({ children }) => {
  const [state, setState] = useState<SidePanelState>({
    open: false,
    mode: 'contacts',
    context: {},
  });

  const { pathname } = useLocation();
  const previousPathnameRef = useRef(pathname);

  // Close side panel when route changes
  useEffect(() => {
    if (previousPathnameRef.current !== pathname) {
      previousPathnameRef.current = pathname;
      setState((prev) => ({
        ...prev,
        open: false,
        actions: [],
        entityInfo: undefined,
        readOnly: false,
      }));
    }
  }, [pathname]);

  // Stable dispatch function - never changes reference
  // When context changes, reset readOnly to false unless the caller explicitly passes it.
  // This prevents readOnly from leaking across different contacts via the spread merge.
  const setStatePartial = useCallback((newState: Partial<SidePanelState>) => {
    setState((prev) => ({
      ...prev,
      ...('context' in newState && !('readOnly' in newState) ? { readOnly: false } : {}),
      ...newState,
    }));
  }, []);

  // Nested providers: dispatch (outer) wraps state (inner)
  // This ensures dispatch context value never changes, preventing re-renders for dispatch-only consumers
  return (
    <SidePanelDispatchContext.Provider value={setStatePartial}>
      <SidePanelStateContext.Provider value={state}>{children}</SidePanelStateContext.Provider>
    </SidePanelDispatchContext.Provider>
  );
};

/**
 * Hook to get side panel state only.
 * Use this when you need to READ state (will re-render when state changes).
 */
export const useSidePanelState = (): SidePanelState => {
  const state = useContext(SidePanelStateContext);
  if (state === null) {
    throw new Error('useSidePanelState must be used within a SidePanelProvider');
  }
  return state;
};

/**
 * Hook to get side panel dispatch only.
 * Use this when you only need to WRITE state (will NOT re-render when state changes).
 * This is the performance-optimized hook for components that only call setSidePanelState.
 */
export const useSidePanelDispatch = (): ((state: Partial<SidePanelState>) => void) => {
  const dispatch = useContext(SidePanelDispatchContext);
  if (dispatch === null) {
    throw new Error('useSidePanelDispatch must be used within a SidePanelProvider');
  }
  return dispatch;
};

/**
 * Hook to optionally get side panel dispatch.
 * Returns null when used outside a SidePanelProvider.
 * Useful for shared components rendered in contexts without a provider (e.g. public pages).
 */
export const useSidePanelDispatchOptional = ():
  | ((state: Partial<SidePanelState>) => void)
  | null => {
  return useContext(SidePanelDispatchContext);
};

/**
 * Hook to get both state and dispatch (backwards compatible).
 * Components using this will re-render when state changes.
 * For better performance, use useSidePanelDispatch() if you only need to write state.
 */
export const useSidePanel = (): SidePanelContextType => {
  const sidePanelState = useSidePanelState();
  const setSidePanelState = useSidePanelDispatch();
  return { sidePanelState, setSidePanelState };
};
